import { useRef, type ChangeEvent } from "react";
import { IconPhoto, IconTrash, IconUpload } from "@tabler/icons-react";
import { Text } from "../../../../components/common/typography";
import { Button } from "../../../../components/common/button";
import { Shimmer } from "../../../../components/common/shimmer";
import { SectionCard } from "./section-card";
import {
  COMPANY_LOGO_MAX_BYTES,
  COMPANY_LOGO_TYPES,
  useCompanyLogo,
} from "../../hooks/useCompanyLogo";
import { showErrorToast, showSuccessToast } from "../../../../utils/toasts";

function extractErrorMessage(err: unknown, fallback: string): string {
  const typed = err as { response?: { data?: { message?: string } } };
  return typed?.response?.data?.message ?? fallback;
}

/**
 * Account → Company profile: upload, replace or remove the company logo.
 * Saved straight away (no admin review — it's branding, not KYC data) and
 * printed on the company's quotation and order PDFs.
 */
export function CompanyLogoCard() {
  const { logoUrl, error, isSaving, upload, remove } = useCompanyLogo();
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFile = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // let the same file be picked again after an error
    if (!file) return;
    if (!(COMPANY_LOGO_TYPES as readonly string[]).includes(file.type)) {
      showErrorToast("The logo must be a PNG or JPEG image.");
      return;
    }
    if (file.size > COMPANY_LOGO_MAX_BYTES) {
      showErrorToast("The logo must be 2 MB or smaller.");
      return;
    }
    try {
      await upload(file);
      showSuccessToast("Company logo saved");
    } catch (err) {
      showErrorToast(extractErrorMessage(err, "Could not upload the logo."), err);
    }
  };

  const handleRemove = async () => {
    try {
      await remove();
      showSuccessToast("Company logo removed");
    } catch (err) {
      showErrorToast(extractErrorMessage(err, "Could not remove the logo."), err);
    }
  };

  return (
    <SectionCard title="Company logo">
      <div className="flex items-center gap-5 max-md:flex-col max-md:items-start max-md:gap-4">
        <div className="flex h-24 w-40 shrink-0 items-center justify-center overflow-hidden rounded-2xl border border-borderDefault bg-bgLight">
          {logoUrl === undefined && !error ? (
            <Shimmer className="h-full w-full" />
          ) : logoUrl ? (
            <img src={logoUrl} alt="Company logo" className="max-h-20 max-w-36 object-contain" />
          ) : (
            <IconPhoto size={28} className="text-textMuted" />
          )}
        </div>
        <div className="flex flex-1 flex-col gap-3 max-md:w-full">
          <Text variant="bodySm" tone="muted">
            {error
              ? "Couldn't load your logo. Please refresh the page."
              : "Shown on your quotation and order PDFs. PNG or JPEG, up to 2 MB."}
          </Text>
          <div className="flex flex-wrap items-center gap-3">
            <input
              ref={inputRef}
              type="file"
              accept={COMPANY_LOGO_TYPES.join(",")}
              className="hidden"
              aria-label="Company logo file"
              onChange={(e) => void handleFile(e)}
            />
            <Button
              variant="secondary"
              size="sm"
              leftIcon={<IconUpload size={16} />}
              disabled={isSaving || logoUrl === undefined}
              onClick={() => inputRef.current?.click()}
            >
              {isSaving ? "Saving…" : logoUrl ? "Replace logo" : "Upload logo"}
            </Button>
            {logoUrl && (
              <Button
                variant="ghost"
                size="sm"
                leftIcon={<IconTrash size={16} />}
                disabled={isSaving}
                onClick={() => void handleRemove()}
              >
                Remove
              </Button>
            )}
          </div>
        </div>
      </div>
    </SectionCard>
  );
}
